/**
 * PallasNavigationRail — Application Sidebar (v2.5 chat-first shell)
 *
 * Always-expanded sidebar: brand header, a small set of workspace tools, a
 * persistent CHATS thread list (the conversation history), and a profile
 * footer whose dropdown surfaces backend service health. Flat single surface,
 * one accent treatment for the active item — the whole app reads as one
 * console rather than a stack of cards.
 */

import React from 'react';
import { EuiIcon } from '@elastic/eui';
import { THEME, THEME_STORAGE_KEY } from '../../utils/theme';
import { MODULE_COLORS, FONT } from './PallasUI';
import PallasLogo from '../../artifacts/pallas-icon-light.svg';

// ─── Routing ───────────────────────────────────────────────────────────────────
// VALID_MODULES is the full set of routable screens (drives URL-hash routing in
// App.js). Some are reachable without a sidebar entry:
//   - `vulns` is hidden
//   - `standup` is launched from the top action strip
//   - `narrative` (Alert Triage) also embeds in the Ageleia app, but is
//     surfaced here too: the Workspace section has an "Alert Triage" entry so
//     analysts can open the Attack-Narrative view directly inside Pallas.

//   - `ioc` (Threat Intel) and `decoder` (Decoder Lab) were retired entirely:
//     the "Tools" group was removed from the rail AND their routes were
//     dropped, so #ioc / #decoder no longer resolve. getInitialModule() in
//     App.js falls any unknown hash back to `chat`, so old bookmarks land on
//     the chat view instead of a blank screen.

export const VALID_MODULES = ['chat', 'narrative', 'vulns', 'standup'];

// Health surfaced in the profile dropdown.
const PROFILE_HEALTH = [
  { key: 'wazuh_manager', label: 'Endpoints' },
  { key: 'suricata',      label: 'Network' },
  { key: 'ollama',        label: 'LLM' },
];

function healthColor(status) {
  if (status === 'connected') return THEME.statusActive;
  if (status === 'partial' || status === 'connecting') return THEME.warning;
  if (status === 'disabled') return THEME.textFaint;
  return THEME.danger;
}

// ─── Brand header ──────────────────────────────────────────────────────────────

function BrandHeader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 12px', flexShrink: 0 }}>
      {/* Cream owl artwork: inverted to dark ink in light mode so it stays
          legible on ivory. No backing tile — the filter would invert it too. */}
      <img
        src={PallasLogo}
        alt="Pallas"
        style={{
          width: 36, height: 36, flexShrink: 0,
          filter: (typeof window !== 'undefined' && window.__pallasTheme?.mode === 'light')
            ? 'invert(0.82) sepia(0.12)' : 'none',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, minWidth: 0 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: THEME.text, fontFamily: FONT.base }}>Pallas</span>
        <span style={{
          fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: THEME.textMuted, marginTop: 2,
        }}>AI Analyst</span>
      </div>
    </div>
  );
}

// ─── Action row — module-style button driven by an explicit label/icon/onClick
// rather than a module id. Used for the workspace strip
// (New Chat, Browse Quick Actions, Daily Standup) at the top of the rail.
// ─────────────────────────────────────────────────────────────────────────────
function ActionRow({ icon, label, accent, isActive, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  const isHighlighted = isActive || hovered;
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
      style={{
        width: '100%', height: 38, display: 'flex', alignItems: 'center', gap: 11,
        padding: '0 12px', margin: '1px 0', cursor: 'pointer', border: 'none', borderRadius: 8,
        backgroundColor: isActive ? accent + '14' : hovered ? THEME.surfaceHover : 'transparent',
        transition: 'background-color 0.15s ease', outline: 'none', position: 'relative', textAlign: 'left',
      }}
    >
      <span style={{
        position: 'absolute', left: 0, top: 7, bottom: 7, width: 3, borderRadius: 3,
        background: isActive ? accent : 'transparent', transition: 'background 0.15s ease',
      }} />
      <EuiIcon type={icon} size="m" color={isHighlighted ? accent : THEME.textMuted} style={{ flexShrink: 0 }} />
      <span style={{
        flex: 1, fontSize: 13, fontWeight: isActive ? 600 : 500,
        color: isActive ? THEME.text : (hovered ? THEME.textBright : THEME.textSecondary),
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{label}</span>
    </button>
  );
}

// ─── Thread (chat) row ─────────────────────────────────────────────────────────

// F3: relative-time helper for the sidebar. Returns short scent-of-activity
// strings — "just now", "5m ago", "3h ago", "2d ago", or a date for older.
// Defensive against missing/invalid timestamps (returns empty string).
function formatRelativeTime(ms) {
  if (!ms || !Number.isFinite(ms)) return '';
  const diff = Date.now() - ms;
  if (diff < 60_000)         return 'just now';
  if (diff < 3_600_000)      return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000)     return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  if (diff < 35 * 86_400_000) return `${Math.floor(diff / (7 * 86_400_000))}w ago`;
  try {
    const d = new Date(ms);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  } catch (_) {
    return '';
  }
}

function ThreadRow({ thread, isActive, onOpen, onDelete, onRename }) {
  const [hovered, setHovered] = React.useState(false);
  // F4: inline rename — double-click the title to enter edit mode. Enter or
  // blur commits; Escape cancels. The name is stored with the chat (every device).
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef(null);
  const accent = MODULE_COLORS.chat.accent;
  // F3: scent-of-activity — number of turns and how recently used. Tolerant
  // of older threads that may have been cached before turnCount existed.
  const turnCount = Number.isFinite(Number(thread?.turnCount))
    ? Number(thread.turnCount)
    : (Array.isArray(thread?.turns) ? thread.turns.length : 0);
  const updatedAt = Number(thread?.updatedAt) || 0;
  const rel = formatRelativeTime(updatedAt);
  const hasMeta = turnCount > 0 || rel;

  const beginEdit = React.useCallback(() => {
    if (typeof onRename !== 'function') return;
    setDraft(thread.title || '');
    setEditing(true);
    // Focus + select on next tick once the input mounts.
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  }, [onRename, thread.title]);

  const commitEdit = React.useCallback(() => {
    const next = (draft || '').trim();
    if (next && next !== (thread.title || '') && typeof onRename === 'function') {
      onRename(thread.id, next);
    }
    setEditing(false);
  }, [draft, onRename, thread.id, thread.title]);

  const cancelEdit = React.useCallback(() => {
    setEditing(false);
    setDraft('');
  }, []);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: hasMeta ? '5px 8px 5px 12px' : '0 8px 0 12px',
        minHeight: hasMeta ? 44 : 34,
        borderRadius: 8, cursor: editing ? 'text' : 'pointer',
        backgroundColor: isActive ? accent + '14' : hovered ? THEME.surfaceHover : 'transparent',
        transition: 'background-color 0.12s ease', position: 'relative',
      }}
      onClick={() => { if (!editing) onOpen(thread.id); }}
    >
      <span style={{
        position: 'absolute', left: 0, top: 7, bottom: 7, width: 3, borderRadius: 3,
        background: isActive ? accent : 'transparent',
      }} />
      <EuiIcon type="discuss" size="s" color={isActive ? accent : THEME.textMuted} style={{ flexShrink: 0 }} />
      <div style={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', gap: 1,
      }}>
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            style={{
              width: '100%',
              fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
              color: THEME.text,
              background: THEME.surfaceHover,
              border: `1px solid ${accent}50`,
              borderRadius: 4,
              padding: '2px 6px',
              outline: 'none',
              lineHeight: 1.25,
            }}
          />
        ) : (
          <span
            onDoubleClick={(e) => { e.stopPropagation(); beginEdit(); }}
            title={typeof onRename === 'function' ? 'Double-click to rename' : undefined}
            style={{
              fontSize: 12.5, fontWeight: isActive ? 600 : 500,
              color: isActive ? THEME.text : THEME.textSecondary,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              lineHeight: 1.25,
            }}
          >
            {thread.title || 'New chat'}
          </span>
        )}
        {hasMeta && !editing && (
          <span style={{
            fontSize: 10, color: THEME.textFaint, fontFamily: FONT.mono,
            display: 'flex', alignItems: 'center', gap: 5,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            lineHeight: 1.2,
          }}>
            {turnCount > 0 && (
              <span>{turnCount} turn{turnCount === 1 ? '' : 's'}</span>
            )}
            {turnCount > 0 && rel && (
              <span style={{ opacity: 0.4 }}>·</span>
            )}
            {rel && <span>{rel}</span>}
          </span>
        )}
      </div>
      {hovered && !editing && (
        <>
          {/* F4: rename trigger — appears on hover for discoverability.
              Double-click on the title also works for keyboard-light users. */}
          {typeof onRename === 'function' && (
            <button
              onClick={(e) => { e.stopPropagation(); beginEdit(); }}
              aria-label="Rename chat"
              title="Rename chat"
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer', padding: 2,
                display: 'flex', alignItems: 'center', flexShrink: 0, outline: 'none',
              }}
            >
              <EuiIcon type="pencil" size="s" color={THEME.textMuted} />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(thread.id); }}
            aria-label="Delete chat"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer', padding: 2,
              display: 'flex', alignItems: 'center', flexShrink: 0, outline: 'none',
            }}
          >
            <EuiIcon type="trash" size="s" color={THEME.textMuted} />
          </button>
        </>
      )}
    </div>
  );
}

// ─── User footer + health dropdown ─────────────────────────────────────────────

// Light/dark switch. Persists to localStorage and reloads — a full reload is
// the one bulletproof way to re-skin an inline-styles app (memoized markdown,
// injected keyframes CSS, EuiProvider colorMode all reset cleanly). Hidden in
// the Ageleia embed, which forces its own dark skin.
function ThemeToggle() {
  const info = (typeof window !== 'undefined' && window.__pallasTheme) || { mode: 'dark', locked: false };
  if (info.locked) return null;
  const isDark = info.mode !== 'light';
  const next = isDark ? 'light' : 'dark';
  const toggle = () => {
    try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch (_) { /* private mode */ }
    window.location.reload();
  };
  return (
    <button
      onClick={toggle}
      title={`Switch to ${next} theme`}
      aria-label={`Switch to ${next} theme`}
      style={{
        width: 26, height: 26, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent',
        border: `1px solid ${THEME.border}`,
        cursor: 'pointer', outline: 'none', flexShrink: 0,
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = THEME.surfaceHover; e.currentTarget.style.borderColor = THEME.borderLight; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = THEME.border; }}
    >
      <EuiIcon type={isDark ? 'sun' : 'moon'} size="s" color={THEME.textMuted} />
    </button>
  );
}

function UserFooter({ user, toolHealth }) {
  const name = user?.displayName || 'Analyst';
  const role = user?.role || 'SOC Analyst';
  const initial = name.trim().charAt(0).toUpperCase() || 'A';

  return (
    <div style={{ borderTop: `1px solid ${THEME.border}`, flexShrink: 0, padding: '12px 14px 14px' }}>
      {/* Profile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center',
          justifyContent: 'center',
          background: `${MODULE_COLORS.chat.accent}14`,
          border: `1px solid ${MODULE_COLORS.chat.accent}40`,
          color: MODULE_COLORS.chat.accent,
          fontSize: 13, fontWeight: 700, flexShrink: 0,
        }}>{initial}</div>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: THEME.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
          <span style={{ fontSize: 10, color: THEME.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{role}</span>
        </div>
        <ThemeToggle />
      </div>

      {/* Service status — always visible, one compact row under the profile */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        marginTop: 12, paddingTop: 10, borderTop: `1px solid ${THEME.borderSubtle}`,
      }}>
        {PROFILE_HEALTH.map(p => {
          const status = toolHealth?.[p.key] || 'disabled';
          const color = healthColor(status);
          return (
            <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              {/* Flat dot — no glow halo (v2.5 retired colored halos). */}
              <span style={{
                width: 7, height: 7, borderRadius: '50%', backgroundColor: color,
                flexShrink: 0,
              }} />
              <span style={{ fontSize: 11.5, color: THEME.textSecondary, whiteSpace: 'nowrap' }}>{p.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── PallasNavigationRail ─────────────────────────────────────────────────────

// F2: Sync badge for the Chats section — renders the usePallasThreads
// backendStatus ('loading' | 'ok' | 'offline') so analysts can tell when the
// list is fresh from OpenSearch vs falling back to the local cache. Without
// this badge the silent-degradation failure mode is invisible.
function SyncBadge({ status }) {
  if (!status) return null;
  const config = {
    ok:      { color: THEME.success, label: 'Synced',  tooltip: 'Chat list loaded from server' },
    loading: { color: THEME.textFaint, label: 'Sync…',  tooltip: 'Loading chat list from server' },
    offline: { color: THEME.warning, label: 'Offline', tooltip: 'Server unreachable — showing cached chats only' },
  }[status];
  if (!config) return null;
  return (
    <span
      title={config.tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        marginLeft: 8, padding: '1px 6px', borderRadius: 4,
        fontSize: 9, fontWeight: 700,
        color: config.color,
        background: `${config.color}14`,
        border: `1px solid ${config.color}30`,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        verticalAlign: 'middle',
      }}
    >
      <span style={{
        width: 5, height: 5, borderRadius: '50%',
        background: config.color,
        animation: status === 'loading' ? 'pallas-pulse 1.5s ease-in-out infinite' : 'none',
      }} />
      {config.label}
    </span>
  );
}

export function PallasNavigationRail({
  activeModule, onModuleChange, toolHealth = {}, user,
  threads = [], activeThreadId, onOpenThread, onDeleteThread,
  // v2.5 action strip — actions that used to live in the top header have
  // moved into the rail so the main column carries no chrome.
  onNewChat,             // () => clear thread + navigate to chat
  onBrowseQuickActions,  // () => open the command palette overlay
  backendStatus,         // F2: 'loading' | 'ok' | 'offline' from usePallasThreads
  // F4: chat-management
  onRenameThread,        // (id, newTitle) => local rename (no backend persist)
  onRefreshThreads,      // () => re-pull threads from backend (async)
}) {
  // F4: refresh button state — brief visual feedback so the analyst sees the
  // click registered even when the network response is fast.
  const [refreshing, setRefreshing] = React.useState(false);
  const handleRefreshClick = React.useCallback(async () => {
    if (refreshing || typeof onRefreshThreads !== 'function') return;
    setRefreshing(true);
    try {
      await onRefreshThreads();
    } finally {
      // Keep the spinner visible for at least ~350ms so a sub-100ms refresh
      // still reads as a click. Without this the spin barely registers.
      setTimeout(() => setRefreshing(false), 350);
    }
  }, [refreshing, onRefreshThreads]);
  return (
    <nav
      aria-label="Primary navigation"
      style={{
        width: 248, minWidth: 248, display: 'flex', flexDirection: 'column',
        // Aligned to the page scheme, a hair darker than the content (#151519)
        // for a subtle panel distinction — not the heavy near-black of bgDeep.
        backgroundColor: THEME.railBg, borderRight: `1px solid ${THEME.border}`,
        height: '100%', overflow: 'hidden', flexShrink: 0,
      }}
    >
      <BrandHeader />

      <div style={{ padding: '0 12px' }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: THEME.textFaint, textTransform: 'uppercase',
          letterSpacing: '0.12em', padding: '8px 12px 6px',
        }}>Workspace</div>

        {/* New Chat, then Explore Skills. Daily Standup moved OUT of the
            workspace section — it's a skill now, reachable from "/" in the
            composer and from Explore Skills. */}
        <ActionRow
          icon="discuss"
          label="New Chat"
          accent={MODULE_COLORS.chat.accent}
          isActive={activeModule === 'chat'}
          onClick={onNewChat}
        />
        <ActionRow
          icon="grid"
          label="Explore Skills"
          accent={MODULE_COLORS.chat.accent}
          onClick={onBrowseQuickActions}
        />
        <ActionRow
          icon="inspect"
          label="Alert Triage"
          accent={MODULE_COLORS.narrative.accent}
          isActive={activeModule === 'narrative'}
          onClick={() => onModuleChange('narrative')}
        />
      </div>

      {/* CHATS / threads — scrollable, fills remaining height */}
      <div style={{
        fontSize: 10, fontWeight: 700, color: THEME.textFaint, textTransform: 'uppercase',
        letterSpacing: '0.12em', padding: '18px 18px 6px 24px', flexShrink: 0,
        display: 'flex', alignItems: 'center',
      }}>
        Chats
        <SyncBadge status={backendStatus} />
        {/* F4: manual refresh — useful after deletes/renames on another
            device or after a known backend hiccup. Self-disables while in
            flight; brief spin shows the click registered. */}
        {typeof onRefreshThreads === 'function' && (
          <button
            onClick={handleRefreshClick}
            disabled={refreshing}
            aria-label="Refresh chat list"
            title="Refresh chat list"
            style={{
              marginLeft: 'auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: 4,
              border: 'none', background: 'transparent',
              cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.5 : 0.7,
              outline: 'none', padding: 0,
            }}
            onMouseEnter={(e) => { if (!refreshing) e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { if (!refreshing) e.currentTarget.style.opacity = '0.7'; }}
          >
            <span style={{
              display: 'inline-flex',
              animation: refreshing ? 'pallas-spin 0.75s linear infinite' : 'none',
            }}>
              <EuiIcon type="refresh" size="s" color={THEME.textMuted} />
            </span>
          </button>
        )}
      </div>
      <div className="pallas-scrollbar" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0 12px 8px' }}>
        {threads.length === 0 ? (
          <div style={{ fontSize: 11.5, color: THEME.textMuted, padding: '6px 12px', lineHeight: 1.5 }}>
            No chats yet. Start one with “New chat”.
          </div>
        ) : (
          threads.map(t => (
            <ThreadRow
              key={t.id}
              thread={t}
              isActive={activeModule === 'chat' && t.id === activeThreadId}
              onOpen={onOpenThread}
              onDelete={onDeleteThread}
              onRename={onRenameThread}
            />
          ))
        )}
      </div>

      <UserFooter user={user} toolHealth={toolHealth} />
    </nav>
  );
}

export default PallasNavigationRail;
